import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { identityApiRef } from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import TextField from '@material-ui/core/TextField';
import useAsync from 'react-use/esm/useAsync';

import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { getGroups, Group } from './getGroups';
import { useEffect, useMemo, useRef, useState } from 'react';

const groupFilter = createFilterOptions<Group>();

export const GitlabRepoLocationPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, rawErrors, uiSchema } = props;
  const [group, setGroup] = useState<Group | null>();
  const proxyBaseurl = useRef('');
  const token = useRef('');
  const apiRef = useApi(discoveryApiRef);
  const identityRef = useApi(identityApiRef);

  const allowedGroups = useMemo(
    () => uiSchema?.['ui:options']?.allowedGroups ?? [],
    [uiSchema],
  ) as string[];

  const allowedGroupsRegex = useMemo(
    () => uiSchema?.['ui:options']?.allowedGroupsRegex ?? '',
    [uiSchema],
  ) as string;

  useEffect(() => {
    apiRef.getBaseUrl('custom-proxy').then(baseUrl => {
      if (baseUrl) {
        proxyBaseurl.current = baseUrl;
      }
    });
  }, [apiRef]);

  useEffect(() => {
    identityRef.getCredentials().then(cred => {
      if (cred?.token) {
        token.current = cred.token;
      }
    });
  }, [identityRef]);

  const { value: groups = [], loading } = useAsync(async () => {
    return await getGroups({
      baseUrl: proxyBaseurl.current,
      token: token.current,
      allowedGroups,
      allowedGroupsRegex,
    });
  }, [proxyBaseurl.current, token.current]);

  if (loading) {
    return <Progress />;
  }

  return (
    <>
      <FormControl margin="normal" required error={rawErrors?.length > 0}>
        <Autocomplete
          value={group}
          selectOnFocus
          clearOnBlur
          onChange={(_, selectedGroup) => {
            if (typeof selectedGroup === 'string') {
              setGroup({
                full_path: selectedGroup,
              });
              onChange(String(selectedGroup));
            } else {
              setGroup(selectedGroup);
              onChange(selectedGroup?.web_url?.replace('/groups', ''));
            }
          }}
          filterOptions={(options, inputGroup) =>
            groupFilter(options, inputGroup)
          }
          getOptionLabel={grp => grp.full_path}
          options={groups}
          renderOption={grp => grp.full_path}
          id="available-groups"
          renderInput={params => (
            <TextField
              {...params}
              label={`${
                group?.web_url
                  ? `Repository Location ${group.web_url.replace(
                      '/groups',
                      '',
                    )}`
                  : 'Repository Location'
              }`}
              variant="outlined"
            />
          )}
        />
        <FormHelperText>
          Select the group/subgroup where the new project should be created in.{' '}
          <br />
          No Private Projects should be created in Infinity and thus no Private
          Group is shown.
          <br />
          For personal or team projects, select your teams subgroup, i.e. "
          <i>teams/scalar/dynamite</i>". <br />
          For SCALAR, select the appropriate module group in the correct layer,
          i.e. "<i>core/assetlinker</i>". <br />
          For guidance on which group/subgroup to use, refer to{' '}
          <a href="https://zf-scalar.atlassian.net/wiki/x/IoCS5Q">
            https://zf-scalar.atlassian.net/wiki/x/IoCS5Q
          </a>
          . <br />
          For guidance on how to create a new group, refer to{' '}
          <a href="https://zf-scalar.atlassian.net/wiki/x/LQC45Q">
            https://zf-scalar.atlassian.net/wiki/x/LQC45Q
          </a>
          .
        </FormHelperText>
      </FormControl>
    </>
  );
};
